import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { Price } from '@adornis/payments/db/prices.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { Document } from 'bson';
import { DateTime } from 'luxon';
import type { InsertOneResult } from 'mongodb';
import { of } from 'rxjs';
import { BundlePrice } from './BundlePrice.js';
import { BHFilter } from './bh-filter.js';
import { Institution } from './institution.js';
import { Product } from './products.js';
import type { User } from './user.js';

@Entity()
export class Bundle extends MongoEntity {
  static override _class = 'Bundle';
  static override _collectionName = 'bundles';

  @validate(nonOptional())
  @Field(type => String)
  name!: string;

  @validate(nonOptional())
  @Field(type => String)
  institutionID!: string;

  @Field(type => Boolean, { default: v => (v === null ? false : !!v) })
  hasPrice?: boolean;

  @validate(async options => {
    await nonOptional()(options);
    const price = options.value as Price;
    const value = price.unitAmount;
    if (value > 100000 * 100)
      throw new ValidationError('Preis darf 100.000 Eur nicht überschreiten', {
        translationKey: 'max_price_err',
        key: options.key,
      });
  })
  @Field(type => Price, { default: v => v ?? new Price({ unitAmount: 1 }) })
  price!: Price;

  get getUnitAmount() {
    return this.hasPrice ? this.price.unitAmount : null;
  }
  get getCalcAmount() {
    return this.hasPrice ? this.price.unitAmount : 0;
  }

  @Field(type => String)
  timeframeID!: string;

  @DataLoadedField(type => Timeframe, ({ timeframeID }) => timeframeID)
  timeframe: Maybe<Timeframe>;

  @validate(nonOptional())
  @Field(type => [String], { default: v => v ?? [] })
  productIDs!: string[];

  @DataLoadedField(type => [Product], ({ productIDs }) => productIDs)
  products: Maybe<Product[]>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  isActive!: boolean;

  @Field(type => DateTime)
  publishDate?: DateTime;

  public get discountInPercent(): number {
    if (!this.getUnitAmount) return 0;
    const priceBefore = (this.products ?? []).reduce(
      (val, product) => (val += product.hasPrice ? product.prices[0]?.unitAmount ?? 0 : 0),
      0,
    );
    if (priceBefore === 0) return 0;
    return (1 - this.getCalcAmount / priceBefore) * 100;
  }

  private async _livediscountInPercent(): Promise<number> {
    const products = await getByID(Product, this.productIDs)({ hasPrice: 1, prices: { unitAmount: 1 } });

    if (!this.getUnitAmount) return 0;
    const priceBefore = products.reduce(
      (val, product) => (val += product.hasPrice ? product.prices[0]?.unitAmount ?? 0 : 0),
      0,
    );
    if (priceBefore === 0) return 0;
    return (1 - this.getCalcAmount / priceBefore) * 100;
  }

  public get discountInCent(): number {
    if (!this.getUnitAmount) return 0;
    const priceBefore = (this.products ?? []).reduce(
      (val, product) => (val += product.hasPrice ? product.prices[0]?.unitAmount ?? 0 : 0),
      0,
    );
    if (priceBefore === 0 || priceBefore < this.getCalcAmount) return 0;
    return priceBefore - this.getCalcAmount;
  }

  public discountToString(): string {
    return `${this.discountInPercent.toFixed(2)}%`;
  }

  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    newEntity: Bundle,
  ) {
    await super.beforeUpdate(oldEntity, newEntity);
    await Bundle.updateCheckProductPrices(newEntity);
  }

  static override async afterCreate(entity: Bundle, result: InsertOneResult<Document>): Promise<void> {
    await super.afterCreate(entity, result);
    await Bundle.updateCheckProductPrices(entity);
  }

  static async updateCheckProductPrices(@Arg('bundle', type => Bundle) bundle: Bundle) {
    let products = await getByID(Product, bundle.productIDs)({ _id: 1, prices: { unitAmount: 1 }, hasPrice: 1 });
    const productCollection = await getRawCollection<EntityData<Product>>(Product._collectionName);
    products = products.filter(product => (product.prices[0]?.unitAmount ?? 0) > 0);

    let cost = 0;
    for (const [index, product] of products.entries()) {
      let price = product.prices.find(price => price instanceof BundlePrice && price.bundleID === bundle._id);
      const discountPrice = product.hasPrice
        ? Number.parseInt(
            ((product.prices[0]?.unitAmount ?? 0) * (1 - (await bundle._livediscountInPercent()) / 100)).toFixed(0),
          )
        : 0;
      if (price) {
        price.unitAmount = discountPrice;
      } else {
        price = new BundlePrice({ _id: A.getGloballyUniqueID(), bundleID: bundle._id, unitAmount: discountPrice });
        product.prices.push(price);
      }

      cost += price.unitAmount;

      if (index === products.length - 1) {
        price.unitAmount = price.unitAmount - (cost - bundle.getCalcAmount);
      }

      await productCollection.updateOne(
        { _id: product._id },
        {
          $set: {
            prices: product.prices.map(price => price.toJSON()),
          },
        },
      );
    }
  }

  @Mutation(type => String)
  static updateForProduct(@Arg('productiD', type => String) productID: string) {
    return async () => {
      const collection = await getCollection<Bundle>(this._class);
      const result = await collection.find<Bundle>({ productIDs: productID }).toArray();
      // triggers update of prices etc
      for (const bundle of result) {
        await (constructValue(bundle) as Bundle).save();
      }
    };
  }

  @Query(type => [Bundle])
  static getAllByInstitution(@Arg('institutionID', type => String) institutionID: string) {
    return async (gqlFields: BaseQLSelectionSet<Bundle>) => {
      const collection = await getCollection<Bundle>(this._class);
      const result = await collection.find<Bundle>({ institutionID }).toArray();
      return result;
    };
  }

  @Subscription(type => [Bundle])
  static subscribeAllBundlesByInstitution(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('institutionID', type => BHFilter) filter: BHFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Bundle>) => {
      if (!filter.institutionID) return of([]);
      return getCollectionHandle(this._collectionName).watchQuery(
        {
          institutionID: filter.institutionID,
          ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
        },
        { skip, limit },
      );
    };
  }

  @Query(type => [Bundle])
  static getValidBundlesForProducts(@Arg('productIDs', type => [String]) productIDs: string[]) {
    return async <SelSet extends BaseQLSelectionSet<Bundle>>(gqlFields: SelSet) => {
      const collection = await getCollection<Bundle>(this._class);
      const result = await collection
        .find<Bundle>({
          $expr: {
            $and: [
              { $setIsSubset: ['$productIDs', productIDs] },
              { $lt: ['$publishDate', new Date()] },
              { $gt: [{ $size: '$productIDs' }, 0] },
            ],
          },
        })
        .toArray();
      return result as NarrowedEntity<Bundle, SelSet>[];
    };
  }

  // TODO typing works for the outside but not on the inside...
  static getSelectedBundles<T extends BaseQLSelectionSet<Bundle> & { productIDs: 1 }>(
    bundles: NarrowedEntity<Bundle, T>[],
    productIDs: string[],
  ) {
    return bundles.filter(bundle => bundle.productIDs.every(productID => productIDs.includes(productID)));
  }

  static async getAllowedBundles() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const institutionIDs = await Institution.getAllowedInstitutions();

    const slotIDs: string[] = [];

    // ADMIN
    if (user.isAdmin) {
      const result = await (
        await getRawCollection(Bundle._collectionName)
      )
        .aggregate<{ _id: string }>([
          {
            $match: {
              institutionID: {
                $in: institutionIDs,
              },
            },
          },
          {
            $project: {
              _id: 1,
            },
          },
        ])
        .toArray();

      slotIDs.push(...result.map(res => res._id));
    }

    return slotIDs;
  }
}
